// ════════════════════════════════════════════════════════════════════════
//  Der Assistent als Rechnung
//
//  Was der Assistent weiß, steht hier; die Schrittkomponenten zeigen es nur an.
//  Damit ist prüfbar, was sonst nur durch Klicken zu sehen wäre: dass aus einer
//  frei getippten Abschnittsangabe ein gültiger Schlüssel wird, und dass kein
//  Schritt weitergeht, solange etwas fehlt.
//
//  Die Abschnittsangabe ist FREIER TEXT, keine hinterlegte Liste (entschieden am
//  2026-08-31). Eine Liste wäre heute vollständig – das Heft ist bis Kapitel 5
//  ausgearbeitet – und mit Kapitel 6 falsch. Sie zu pflegen hieße, Inhalt in den
//  Code zu legen.
// ════════════════════════════════════════════════════════════════════════

import { fingerIndex, type Bundle, type Lesson, type Stage } from "@otto/lehrgang";
import { newLessonKey, newStageKey } from "../../../shared/lib/contentKeys.js";

export type DraftStage = {
  readonly title: string;
  readonly hint: string;
  readonly text: string;
};

export type NewLesson = {
  /** Frei getippt: „4c“, „Heft 4c“, „Kapitel 6, Abschnitt a“ – oder leer. */
  readonly section: string;
  readonly title: string;
  readonly subtitle: string;
  readonly subtitleEdited: boolean;
  readonly color: string;
  readonly intro: string;
  readonly keys: readonly string[];
  readonly uppercaseWith?: "shift" | "capsLock" | undefined;
  readonly stages: readonly DraftStage[];
};

export const STEPS = ["Herkunft", "Tasten", "Einführung", "Stufen", "Prüfen"] as const;
export type Step = (typeof STEPS)[number];

export function emptyLesson(): NewLesson {
  return {
    section: "",
    title: "",
    subtitle: "",
    subtitleEdited: false,
    color: "#8CA3B5",
    intro: "",
    keys: [],
    stages: [{ title: "", hint: "", text: "" }],
  };
}

/**
 * Aus Text ein Stück Schlüssel machen.
 *
 * Das Schema erlaubt nur `[a-z0-9]`, getrennt durch `-` oder `.`. Umlaute
 * werden aufgelöst statt entfernt: aus „Übungen“ wird `uebungen` und nicht
 * `bungen`.
 */
export function slug(text: string): string {
  return text
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Der Untertitelvorschlag: „Heft 4c · Titel“.
 *
 * Er trägt den Buchabschnitt, damit „macht 4c“ in der Lern-App auffindbar ist –
 * das ist der Grund, warum es das Feld überhaupt gibt.
 */
export function suggestSubtitle(state: NewLesson): string {
  const section = stripHeft(state.section).trim();
  if (!section) return state.title;
  return state.title ? `Heft ${section} · ${state.title}` : `Heft ${section}`;
}

/** „Heft 4c“ und „heft 4c“ sollen nicht zu „Heft Heft 4c“ führen. */
function stripHeft(section: string): string {
  return section.replace(/^\s*heft\s+/i, "").trim();
}

/**
 * Was noch fehlt, damit dieser Schritt fertig ist.
 *
 * Gibt Sätze zurück, keine Wahrheitswerte: ein gesperrter Knopf ohne Grund ist
 * eine Sackgasse, und der Grund gehört an die Stelle, an der gesperrt wird.
 */
export function stepProblems(step: Step, state: NewLesson, bundle: Bundle): string[] {
  const problems: string[] = [];

  if (step === "Herkunft") {
    // Kein Wort mehr über Schlüssel: den vergibt das System, und er ist
    // zufällig – es gibt daran nichts zu prüfen und nichts zu wählen.
    if (!state.title.trim()) problems.push("Die Lektion braucht einen Titel.");
  }

  if (step === "Tasten") {
    if (state.keys.length === 0) problems.push("Wählen Sie mindestens eine Taste.");
    const fingers = fingerIndex(bundle);
    for (const key of state.keys) {
      if (!fingers.has(key)) {
        problems.push(`Die Taste "${label(key)}" hat noch keine Finger-Zuordnung.`);
      }
    }
  }

  if (step === "Einführung") {
    // Gegen den WIRKSAMEN Untertitel, nicht gegen das Feld: solange niemand ihn
    // überschrieben hat, gilt der Vorschlag - und der steht sichtbar im Feld.
    // Vorher sperrte der Knopf, während daneben etwas Ausgefülltes stand.
    if (!effectiveSubtitle(state).trim()) problems.push("Der Untertitel fehlt.");
    if (!state.intro.trim()) problems.push("Die Einleitung fehlt.");
  }

  if (step === "Stufen") {
    if (state.stages.length === 0) problems.push("Mindestens eine Stufe.");
    state.stages.forEach((stage, i) => {
      const at = `Stufe ${i + 1}`;
      if (!stage.title.trim()) problems.push(`${at}: der Titel fehlt.`);
      if (!stage.hint.trim()) problems.push(`${at}: der Hinweis an das Kind fehlt.`);
      if (!stage.text.trim()) problems.push(`${at}: der Übungstext fehlt.`);
      for (const char of new Set(stage.text)) {
        if (!state.keys.includes(char)) {
          problems.push(`${at}: "${label(char)}" steht nicht in den Tasten der Lektion.`);
        }
      }
    });
  }

  return problems;
}

/** Der Untertitel, der wirklich benutzt wird. */
export function effectiveSubtitle(state: NewLesson): string {
  return state.subtitleEdited ? state.subtitle : suggestSubtitle(state);
}

/**
 * Aus dem Schrittzustand die fertige Lektion.
 *
 * Nummer und `order` kommen aus dem Bestand: die neue Lektion kommt ans Ende.
 * Umsortieren geht danach in der Arbeitsfläche – der Assistent soll nicht auch
 * noch die Reihenfolge verhandeln.
 */
export function toLesson(
  state: NewLesson,
  bundle: Bundle,
  published: ReadonlySet<string> = new Set(),
): Lesson {
  const key = newLessonKey(bundle, published);

  // Jeder Schlüssel wird gegen die schon vergebenen geprüft – auch gegen die
  // der Stufen, die in dieser Schleife gerade erst entstehen.
  const taken = new Set([...published, key]);
  const stages: Stage[] = [];
  for (const stage of state.stages) {
    const stageKey = newStageKey(bundle, taken);
    taken.add(stageKey);
    stages.push({
      key: stageKey,
      title: stage.title.trim(),
      hint: stage.hint.trim(),
      text: stage.text,
    });
  }

  return {
    key,
    number: bundle.lessons.length + 1,
    order: Math.max(-1, ...bundle.lessons.map((l) => l.order)) + 1,
    title: state.title.trim(),
    subtitle: effectiveSubtitle(state).trim(),
    color: state.color,
    keys: [...state.keys],
    intro: state.intro.trim(),
    ...(state.uppercaseWith ? { uppercaseWith: state.uppercaseWith } : {}),
    stages,
  };
}

/** Die Leertaste ist im Text nicht zu sehen – überall gleich benannt. */
export function label(key: string): string {
  return key === " " ? "␣" : key;
}
